import React, { useCallback, useEffect } from 'react';
import ErrorBoundary from '@atlassian/jira-error-boundary/src/ErrorBoundary.tsx';
import { useIsAnyAppLoading } from '@atlassian/jira-forge-action-apps-controller/src/index.tsx';
import {
	ForgeAppsRuntimeContainer,
	type ForgeAppRenderer,
} from '@atlassian/jira-forge-action-apps-controller/src/runtime-container.tsx';
import { AsyncBoardAction } from '@atlassian/jira-forge-ui-async/src/ui/board-action/async.tsx';
import type { BoardActionExtensionData } from '@atlassian/jira-forge-ui-types/src/common/types/extension-data.tsx';
import type { BoardAction } from '@atlassian/jira-forge-ui-types/src/common/types/extension.tsx';

const BoardActionAppRenderer: ForgeAppRenderer<BoardAction, BoardActionExtensionData> = ({
	app,
	setLoaded,
	localId,
	handleError: baseHandleError,
	closeApp,
}) => {
	const handleError = useCallback(() => {
		baseHandleError && baseHandleError();
	}, [baseHandleError]);

	return (
		<AsyncBoardAction
			localId={localId}
			extension={app.extension}
			extensionData={app.extensionData}
			analyticsAttributes={{ source: '' }}
			onLoad={setLoaded}
			onClose={closeApp}
			onInitialRender={setLoaded}
			onError={handleError}
		/>
	);
};

type BoardActionForgeRuntimeContainerProps = {
	onAppsFinishLoading?: () => void;
};

const BoardActionForgeRuntimeContainer: React.FC<BoardActionForgeRuntimeContainerProps> = ({
	onAppsFinishLoading,
}) => {
	const [isAnyForgeAppLoading] = useIsAnyAppLoading();

	useEffect(() => {
		// this is to close the dropdown when all forge apps are loaded
		if (onAppsFinishLoading && !isAnyForgeAppLoading) {
			onAppsFinishLoading();
		}
	}, [isAnyForgeAppLoading, onAppsFinishLoading]);

	return <ForgeAppsRuntimeContainer forgeAppRenderer={BoardActionAppRenderer} />;
};

export const BoardActionForgeRuntimeContainerWithErrorBoundary: React.FC<
	BoardActionForgeRuntimeContainerProps
> = ({ onAppsFinishLoading }) => {
	return (
		<ErrorBoundary packageName="software" id="forge-board-action-runtime-container">
			<BoardActionForgeRuntimeContainer onAppsFinishLoading={onAppsFinishLoading} />
		</ErrorBoundary>
	);
};
